import { splitDatabaseClient } from './mediaDatabase';
import { createNeonQueryClient } from './neonQuery';
import { getApiBaseUrl } from './neon';
import { trackedFetch } from './loadingProgress';
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL_ENV_KEYS = [
  'REACT_APP_SUPABASE_URL',
  'VITE_SUPABASE_URL',
  'NEXT_PUBLIC_SUPABASE_URL',
  'SUPABASE_URL',
];

const SUPABASE_ANON_KEY_ENV_KEYS = [
  'REACT_APP_SUPABASE_ANON_KEY',
  'REACT_APP_SUPABASE_PUBLISHABLE_KEY',
  'REACT_APP_SUPABASE_KEY',
  'VITE_SUPABASE_ANON_KEY',
  'NEXT_PUBLIC_SUPABASE_ANON_KEY',
  'SUPABASE_ANON_KEY',
  'SUPABASE_KEY',
];

function readFirstEnv(keys) {
  for (const key of keys) {
    const value = process.env[key];
    if (typeof value === 'string' && value.trim()) {
      return {
        key,
        value: value.trim(),
      };
    }
  }

  return {
    key: null,
    value: '',
  };
}

const resolvedSupabaseUrl = readFirstEnv(SUPABASE_URL_ENV_KEYS);
const resolvedSupabaseAnonKey = readFirstEnv(SUPABASE_ANON_KEY_ENV_KEYS);

const supabaseUrl = resolvedSupabaseUrl.value;
const supabaseKey = resolvedSupabaseAnonKey.value;

export const supabaseEnv = {
  url: supabaseUrl,
  anonKey: supabaseKey,
  urlKey: resolvedSupabaseUrl.key,
  anonKeyKey: resolvedSupabaseAnonKey.key,
};

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseKey);

const authClient = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseKey, { global: { fetch: trackedFetch } })
  : null;

// Only catalog/media metadata queries go to Neon. User data and Auth stay Supabase.
async function requestNeon(path, body) {
  const base = getApiBaseUrl();
  try {
    const response = await trackedFetch(`${base}/api/neon-data/${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return await response.json();
  } catch (error) { return { data: null, error: { message: error.message || 'Unable to reach the Neon media API.' } }; }
}
const neonMedia = createNeonQueryClient(
  (spec) => requestNeon('query', spec), (name, args = {}) => requestNeon(`rpc/${encodeURIComponent(name)}`, args)
);
export const supabase = authClient ? splitDatabaseClient(authClient, neonMedia) : null;

// A second client with session persistence off, used for one-shot signUp()
// calls made on someone else's behalf (e.g. an admin creating an account).
// auth.signUp() logs in as the new user on whatever client it's called on —
// running it here instead of the main `supabase` client keeps the caller's
// own session untouched.
let sessionlessClient = null;
export function getSessionlessSupabaseClient() {
  if (!isSupabaseConfigured) {
    throw new Error(getSupabaseConfigErrorMessage());
  }

  if (!sessionlessClient) {
    sessionlessClient = createClient(supabaseUrl, supabaseKey, {
      global: { fetch: trackedFetch },
      auth: {
        // A distinct storageKey gives this client its own Navigator LockManager
        // lock name — without it, GoTrueClient derives the lock from the project
        // URL alone, so this client fights the main `supabase` client for the
        // same lock ("Lock ... was released because another request stole it").
        storageKey: 'sb-admin-signup-auth-token',
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    });
  }

  return sessionlessClient;
}

export function getSupabaseConfigErrorMessage() {
  return [
    'Missing Supabase environment variables.',
    'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY for this Create React App project.',
    'If you are reusing config from another stack, VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY and NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY are also recognized when available.',
  ].join(' ');
}

export function requireSupabaseClient() {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(getSupabaseConfigErrorMessage());
  }

  return supabase;
}

let sessionRequestPromise = null;
let userRequestPromise = null;
let refreshRequestPromise = null;
let authOperationQueue = Promise.resolve();

function runSerializedAuthOperation(operation) {
  const run = authOperationQueue.catch(() => {}).then(operation);
  authOperationQueue = run.catch(() => {});
  return run;
}

// GoTrueClient serializes its own critical sections (init, token refresh,
// session save) behind a single Navigator LockManager lock keyed off the
// project URL. That lock is shared across every caller on the page —
// our own getSession/getUser/refreshSession calls, the SDK's internal
// auto-refresh timer, and onAuthStateChange's internal notifications all
// queue for the same name. If whichever caller is holding it runs long
// (slow network, a dev-only React Strict Mode double-mount, etc.), a
// waiting caller times out and force-steals the lock, and the original
// holder's call rejects with "Lock ... was released because another
// request stole it". That's transient contention, not a real failure —
// retrying picks up an uncontended lock a moment later.
function isLockStolenError(error) {
  return /lock ".*" was released because another request stole it/i.test(String(error?.message || ''));
}

async function withLockStealRetry(operation, retries = 2) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (isLockStolenError(error) && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
}

export async function getSupabaseSession() {
  if (!isSupabaseConfigured || !supabase) {
    return { data: { session: null }, error: null };
  }

  const client = requireSupabaseClient();
  if (!sessionRequestPromise) {
    sessionRequestPromise = runSerializedAuthOperation(() =>
      withLockStealRetry(() => client.auth.getSession())
    ).finally(() => {
      sessionRequestPromise = null;
    });
  }

  return sessionRequestPromise;
}

export async function getSupabaseUser() {
  if (!isSupabaseConfigured || !supabase) {
    return { data: { user: null }, error: null };
  }

  const client = requireSupabaseClient();
  if (!userRequestPromise) {
    userRequestPromise = runSerializedAuthOperation(() =>
      withLockStealRetry(() => client.auth.getUser())
    ).finally(() => {
      userRequestPromise = null;
    });
  }

  return userRequestPromise;
}

export async function refreshSupabaseSession() {
  if (!isSupabaseConfigured || !supabase) {
    return { data: { session: null, user: null }, error: null };
  }

  const client = requireSupabaseClient();
  if (!refreshRequestPromise) {
    refreshRequestPromise = runSerializedAuthOperation(() =>
      withLockStealRetry(() => client.auth.refreshSession())
    ).finally(() => {
      refreshRequestPromise = null;
    });
  }

  return refreshRequestPromise;
}

function looksLikeNetworkError(message) {
  return /failed to fetch|networkerror|network request failed|load failed|fetch failed|network issue/i.test(message);
}

function looksLikeInvalidSupabaseUrl(message) {
  return /invalid url|failed to construct 'url'|must be a valid url|supabase url/i.test(message);
}

function looksLikeInvalidSupabaseKey(message) {
  return /invalid api key|invalid jwt|jwt malformed|apikey|anon key|publishable key/i.test(message);
}

function looksLikeAuthSessionError(message) {
  return /auth session missing|auth session|invalid refresh token|refresh token|jwt expired|session missing|session expired|invalid claim/i.test(message);
}

function looksLikeMissingAuthorizationHeader(message) {
  return /missing authorization header|authorization header/i.test(message);
}

function looksLikeRlsError(message) {
  return /row-level security|permission denied|new row violates row-level security policy|violates row-level security/i.test(message);
}

function looksLikeMissingRelationError(code, message) {
  return code === '42P01' || /relation .* does not exist|table .* does not exist|schema cache/i.test(message);
}

export function toSupabaseError(error, fallbackMessage, options = {}) {
  if (!error) {
    return new Error(fallbackMessage);
  }

  const message = String(error.message || '').trim();
  const code = String(error.code || '').trim();
  const resourceName = options.resourceName || options.resource || 'resource';
  const edgeFunctionName = options.edgeFunctionName || options.functionName || '';

  if (!isSupabaseConfigured) {
    return new Error(getSupabaseConfigErrorMessage());
  }

  if (looksLikeInvalidSupabaseUrl(message)) {
    return new Error('The Supabase URL is invalid. Check REACT_APP_SUPABASE_URL.');
  }

  if (looksLikeInvalidSupabaseKey(message)) {
    return new Error('The Supabase anon key was rejected. Check REACT_APP_SUPABASE_ANON_KEY.');
  }

  if (looksLikeAuthSessionError(message)) {
    return new Error('Your Supabase auth session is missing or invalid. Please sign in again.');
  }

  if (edgeFunctionName && looksLikeMissingAuthorizationHeader(message)) {
    return new Error(
      `The Supabase Edge Function "${edgeFunctionName}" requires a bearer token. Disable JWT verification for that function or make sure the user has an active Supabase session.`
    );
  }

  if (looksLikeMissingRelationError(code, message)) {
    return new Error(`Supabase is missing the required "${resourceName}" table or view. Run the latest migration and verify the schema exists.`);
  }

  if (looksLikeRlsError(message) || code === '42501') {
    return new Error(`Supabase denied access to "${resourceName}". Check the table's RLS policies.`);
  }

  if (edgeFunctionName && /function|functions/i.test(message)) {
    return new Error(`The Supabase Edge Function "${edgeFunctionName}" failed. Check the function deployment and logs.`);
  }

  if (looksLikeNetworkError(message) || error.name === 'TypeError') {
    return new Error('Unable to reach Supabase. Check your Supabase URL, anon key, and network connection.');
  }

  return new Error(message || fallbackMessage);
}

async function getSupabaseFunctionHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    apikey: supabaseKey,
  };

  if (!supabase) {
    return headers;
  }

  try {
    const { data } = await getSupabaseSession();
    const accessToken = data?.session?.access_token;
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  } catch {}

  return headers;
}

function isJwtAuthError(response, data) {
  const message =
    typeof data === 'string'
      ? data
      : data && typeof data === 'object'
        ? [data.error, data.message, data.code].filter(Boolean).join(' ')
        : '';

  return response.status === 401 && /jwt|authorization|token/i.test(message);
}

async function retryFunctionWithRefresh(functionName, body) {
  if (!supabase) {
    return null;
  }

  try {
    const { data, error } = await refreshSupabaseSession();
    if (error || !data?.session?.access_token) {
      return null;
    }
  } catch {
    return null;
  }

  const retryHeaders = await getSupabaseFunctionHeaders();
  return trackedFetch(`${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${encodeURIComponent(functionName)}`, {
    method: 'POST',
    headers: retryHeaders,
    body: JSON.stringify(body ?? {}),
  });
}

async function parseFunctionResponse(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function buildFunctionErrorMessage(response, data) {
  if (data && typeof data === 'object') {
    return data.error || data.message || data.code || `Function request failed with status ${response.status}`;
  }

  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  return `Function request failed with status ${response.status}`;
}

export async function invokeSupabaseFunction(functionName, body) {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(getSupabaseConfigErrorMessage());
  }

  const headers = await getSupabaseFunctionHeaders();
  let response;

  try {
    response = await trackedFetch(`${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${encodeURIComponent(functionName)}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
    });
  } catch (error) {
    throw toSupabaseError(error, `Unable to reach the Supabase Edge Function "${functionName}".`, {
      resourceName: 'edge function',
      edgeFunctionName: functionName,
    });
  }

  let data = await parseFunctionResponse(response);

  if (isJwtAuthError(response, data)) {
    const retryResponse = await retryFunctionWithRefresh(functionName, body);
    if (retryResponse) {
      data = await parseFunctionResponse(retryResponse);
      if (!retryResponse.ok) {
        throw new Error(buildFunctionErrorMessage(retryResponse, data));
      }
      return data;
    }
  }

  if (!response.ok) {
    throw toSupabaseError(
      new Error(buildFunctionErrorMessage(response, data)),
      `The Supabase Edge Function "${functionName}" failed.`,
      {
        resourceName: 'edge function',
        edgeFunctionName: functionName,
      }
    );
  }

  return data;
}

